import { LOCALE_COMMON_PATHS } from 'types/locales';

/**
 * Generates structured data for localized subheading on the area listings page.
 * 
 * This function returns translation key and interpolation values for the subheading
 * that shows either the number of listings found or a fallback message when no
 * listings are available in the requested area.
 * 
 * @param params - Object containing:
 *   - count: Number of listings found
 *   - hasNoListingsInArea: Whether the requested area has no listings
 *   - requestedAreaLabel: Label for the requested area (used in fallback, may be empty string)
 *   - electedAreaLabel: Label for the elected/fallback area (used in fallback)
 *   - useTheArticle: Whether to use "the" article before elected area (e.g., "the USA")
 * 
 * @returns Object with:
 *   - key: Translation key path
 *   - values: Object with interpolation values (count, requestedArea, electedArea)
 *   - count: Number for pluralization (optional)
 * 
 */
interface GenerateLocalizedSubheadingParams {
  count: number;
  hasNoListingsInArea: boolean;
  requestedAreaLabel: string;
  electedAreaLabel: string;
  useTheArticle?: boolean;
}

export interface LocalizedSubheadingData {
  key: string;
  values: {
    count?: number;
    requestedArea?: string;
    electedArea?: string;
  };
  count?: number;
}

export default function generateLocalizedSubheading({
  count,
  hasNoListingsInArea,
  requestedAreaLabel,
  electedAreaLabel,
  useTheArticle = false,
}: GenerateLocalizedSubheadingParams): LocalizedSubheadingData {
  if (hasNoListingsInArea) {
    // No listings in requested area - show fallback message
    if (requestedAreaLabel && requestedAreaLabel.length > 0) {
      const key = useTheArticle 
        ? LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_NO_LISTINGS_FALLBACK_WITH_THE
        : LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_NO_LISTINGS_FALLBACK;
      
      return {
        key,
        values: {
          requestedArea: requestedAreaLabel,
          electedArea: electedAreaLabel,
        },
      };
    } else {
      const key = useTheArticle
        ? LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_NO_LISTINGS_GENERIC_WITH_THE
        : LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_NO_LISTINGS_GENERIC;
      
      return {
        key,
        values: {
          electedArea: electedAreaLabel,
        },
      };
    }
  } else {
    // Listings found - show count with proper pluralization
    return {
      key: LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_LISTINGS_FOUND,
      values: {
        count,
      },
      count, // This enables i18next pluralization
    };
  }
}

